import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../client";
import { qk } from "../keys";
import type { AlertLevel, RiskLatest, RiskLatestNode, RiskSample } from "../models";
import { RISK_FRESH_MS, riToLevel } from "@/shared/lib/levelTone";

interface RiskSampleList {
  count?: number;
  next: string | null;
  previous: string | null;
  results: RiskSample[];
}

/**
 * HTTP 상태 — 응답 인터셉터가 오류를 `ApiError{status}` 로 바꿔 던지므로 `status` 를 먼저 본다.
 * (`err.response.status` 만 보면 항상 undefined 라 404 판정이 한 번도 맞지 않는다.)
 */
function httpStatus(err: unknown): number | undefined {
  const e = err as { status?: unknown; response?: { status?: number } } | null | undefined;
  if (typeof e?.status === "number") return e.status;
  return e?.response?.status;
}
const isNotFound = (err: unknown) => httpStatus(err) === 404;

/**
 * Risk Index time series for a site. Polls every 30 s — the D-S fusion
 * engine emits a fresh sample on each sensor batch, so a slow poll keeps
 * the page current without a dedicated socket.
 */
export function useRiskSamples(siteId: number | undefined) {
  return useQuery({
    queryKey: qk.safety.risk.list({ site: siteId }),
    queryFn: () =>
      api
        // `site_id` — RiskSample's site is a plain column, not a FK, so `site`
        // is not a valid filter name and was being ignored (every site's
        // samples came back). See useControlData.ts for the same fix.
        .get<RiskSampleList>("/risk-samples/", { params: { site_id: siteId } })
        .then((r) => r.data),
    enabled: !!siteId,
    refetchInterval: 30_000,
  });
}

export type RiskWindow = "1h" | "6h" | "24h" | "7d";

export interface RiskSeriesPoint {
  t: string;
  ri_max: number;
  ri_avg: number;
  level: number;
  n: number;
}
export interface RiskSeries {
  window: RiskWindow;
  bucket: string;
  from: string;
  to: string;
  points: RiskSeriesPoint[];
}

/**
 * 동적 RI 추이 — 서버가 창(1h·6h·24h·7d)을 묶음마다 최댓값·평균으로 줄여 준다
 * (`GET /risk-samples/series/`, 백엔드 3.0.25~). 옛 서버면 404 → isError.
 */
export function useRiskSeries(siteId: number | undefined, window: RiskWindow, nodeId?: number | null) {
  const params = { site_id: siteId, window, ...(nodeId ? { node_id: nodeId } : {}) };
  return useQuery({
    queryKey: qk.safety.risk.series(params),
    queryFn: () => api.get<RiskSeries>("/risk-samples/series/", { params }).then((r) => r.data),
    enabled: !!siteId,
    refetchInterval: window === "1h" ? 30_000 : 120_000,
    retry: (n, err: unknown) => n < 1 && !isNotFound(err),
  });
}

/** `useRiskLatest` 가 돌려주는 한 벌 — 서버 응답에 「브라우저가 받은 순간」을 더한 것. */
export type RiskLatestData = RiskLatest & {
  /** 이 응답이 도착한 브라우저 시각(ms). `age_s` 와 더해 프레임 시각을 브라우저 시계로 옮긴다. */
  fetched_at_ms: number;
};

export interface RiskLatestResult {
  data: RiskLatestData | undefined;
  /**
   * 서버가 아직 `/risk-samples/latest/` 를 모르는(404, 백엔드 개편 P5 이전) 경우 — 표본 목록에서
   * 노드별 최신 1건을 골라 같은 모양으로 만든 값이다. 이때는 가스별 풀이(`gases`)가 없고,
   * 나이는 브라우저 시계로 잰다.
   */
  fallback: boolean;
  isError: boolean;
}

/**
 * 현장의 **노드별 최신 위험도**(`GET /risk-samples/latest/?site_id=`, 30초 폴링).
 *
 * 관제 화면의 첫 그림과 소켓이 끊긴 동안의 바탕이다 — 실시간 값은 `risk.updated` 소켓이 채우고
 * (SiteRiskBridge → liveRiskStore), 이 조회는 그 사이를 메운다.
 *
 * 404 폴백: 백엔드가 아직 올라가지 않았으면 `useRiskSamples` 와 **같은 쿼리키**로 표본 목록을
 * 읽어 노드마다 가장 새 표본을 고른다(관제 화면은 그 키를 이미 30초마다 받고 있어 요청이
 * 늘지 않는다). 404 가 난 뒤에도 10분마다 `latest` 를 다시 두드려, 백엔드가 올라가면
 * 새로고침 없이 넘어간다.
 *
 * ⚠️ 폴백의 한계: 표본 목록은 현장 전체 최신 50건이라, 다른 노드가 계속 보고하는 동안 1분 넘게
 *    조용한 노드는 목록에서 빠진다. 이미 받은 프레임은 스토어에 남아 180초 뒤 「갱신 없음」이 되지만,
 *    화면을 **새로 연 직후**에는 그 노드가 「위험도 계산 전」으로 보인다. 가스별 풀이·복합 가스
 *    사유·서버 시계 기준 나이는 `latest` 에만 있다.
 */
export function useRiskLatest(siteId: number | null | undefined): RiskLatestResult {
  const id = siteId ?? undefined;
  const latestQ = useQuery({
    queryKey: qk.safety.risk.latest({ site: id }),
    queryFn: () =>
      api
        .get<RiskLatest>("/risk-samples/latest/", { params: { site_id: id } })
        .then((r): RiskLatestData => ({ ...r.data, fetched_at_ms: Date.now() })),
    enabled: !!id,
    refetchInterval: (q) => (isNotFound(q.state.error) ? 600_000 : 30_000),
    retry: (n, err: unknown) => n < 1 && !isNotFound(err),
  });
  const missing = isNotFound(latestQ.error);
  // 폴백이 아닐 때는 키가 {site: undefined} 인 비활성 쿼리라 요청이 나가지 않는다.
  const samplesQ = useRiskSamples(missing ? id : undefined);
  const samples = samplesQ.data?.results;
  const samplesAt = samplesQ.dataUpdatedAt;

  const data = useMemo<RiskLatestData | undefined>(() => {
    if (!missing) return latestQ.data;
    if (id == null || !samples) return undefined;
    return latestFromSamples(id, samples, samplesAt || Date.now());
  }, [missing, latestQ.data, id, samples, samplesAt]);

  return {
    data,
    fallback: missing,
    isError: missing ? samplesQ.isError : latestQ.isError,
  };
}

/**
 * 표본 목록(최신순) → `RiskLatest` 모양. 노드마다 처음 만난 표본이 가장 새 것이다.
 * 목록은 실측(`is_drill=false`)만 준다 — 그래서 `drill` 은 항상 false.
 */
function latestFromSamples(siteId: number, samples: readonly RiskSample[], fetchedAt: number): RiskLatestData {
  const staleAfterS = RISK_FRESH_MS / 1000;
  const seen = new Set<number>();
  const nodes: RiskLatestNode[] = [];
  for (const s of samples) {
    if (s.node_id == null || seen.has(s.node_id)) continue;
    seen.add(s.node_id);
    const t = Date.parse(s.time);
    // 브라우저 시계가 서버보다 늦으면 나이가 음수가 된다 — 0 으로 자른다.
    const ageS = Number.isFinite(t) ? Math.max(0, (fetchedAt - t) / 1000) : Number.POSITIVE_INFINITY;
    if (ageS > 24 * 3600) continue;
    nodes.push({ ...s, age_s: ageS, stale: ageS > staleAfterS, is_drill: false });
  }
  let worst: RiskLatestNode | undefined;
  for (const n of nodes) {
    if (n.stale) continue;
    if (!worst || n.ri_dynamic > worst.ri_dynamic) worst = n;
  }
  const siteRi = worst ? worst.ri_dynamic : null;
  return {
    site_id: siteId,
    drill: false,
    now: new Date(fetchedAt).toISOString(),
    stale_after_s: staleAfterS,
    site_ri: siteRi,
    site_level: siteRi == null ? null : (riToLevel(siteRi) as AlertLevel),
    worst_node_id: worst?.node_id ?? null,
    nodes,
    fetched_at_ms: fetchedAt,
  };
}
